import { zodResolver } from '@hookform/resolvers/zod';
import {
  AccountTypeSchema, isLiability, OFFSET_SOURCE_TYPES, SECURED_LOAN_TYPES, TaxWrapperSchema, todayISO, VALUED_ASSET_TYPES, type Account, type AccountType,
} from '@keel/core';
import { useMutation } from '@tanstack/react-query';
import { useEffect } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useNavigate } from 'react-router';
import { z } from 'zod';
import { errorMessage, useTRPC } from '@/api/query';
import {
  Button, Checkbox, DateInput, Dialog, DialogContent, Field, Input, MoneyInput, NumberInput, Select, Switch, Textarea, toast,
} from '@/components/ui';
import { CURRENCY_OPTIONS } from '@/lib/countries';
import { useLedger, useRefresh } from './data';
import { useFeatures } from '@/lib/features';
import { ACCOUNT_TYPE_OPTIONS, taxWrapperOptions } from './labels';
import { ColorPicker, IconPicker } from './pickers';
import { localTerms } from '@/lib/localise';
import { BRAND } from '@/brand';

export interface AccountDialogProps {
  readonly open: boolean;
  readonly onOpenChange: (open: boolean) => void;
  /** Edit this account; omit to create. */
  readonly account?: Account | null;
  /** Prefill for a new account (e.g. `{ type: 'investment', name: 'nabtrade' }`). */
  readonly defaults?: Partial<Account>;
  readonly onSaved?: (account: Account) => void;
  /** New property: loans to secure against it once saved (e.g. from the "Add the property this loan is for" prompt). */
  readonly linkLoanIds?: readonly string[];
}

/**
 * Gentle CTA after a mortgage arrives (created, or linked from a bank feed): add the property it is secured against.
 * Opens the Accounts page's add dialog prefilled as a property linked to this loan.
 */
export const promptAddProperty = (loan: Pick<Account, 'id' | 'name'>, navigate: (to: string) => void): void => {
  toast('Add the property this loan is for', {
    description: `Track its value next to ${loan.name} to see your equity and LVR.`,
    duration: 12_000,
    action: { label: 'Add property', onClick: () => navigate(`/accounts?new=property&loan=${encodeURIComponent(loan.id)}`) },
  });
};
const NONE = '__none';

const AU_ONLY_TYPES: ReadonlySet<AccountType> = new Set(['offset', 'superannuation', 'hecs_help']);
const BSB_TYPES: ReadonlySet<AccountType> = new Set(['transaction', 'savings', 'offset', 'term_deposit']);
const RATE_TYPES: ReadonlySet<AccountType> = new Set(['savings', 'offset', 'term_deposit', 'loan', 'mortgage', 'credit_card', 'hecs_help']);
const WRAPPER_TYPES: ReadonlySet<AccountType> = new Set(['investment', 'savings', 'superannuation', 'cash']);
const options = (pairs: readonly (readonly [string, string])[]) => pairs.map(([value, label]) => ({ value, label }));
/** Brokers people in each country actually use (the list follows the entity's country). */
const BROKERS: Readonly<Record<string, { value: string; label: string }[]>> = {
  AU: options([['nabtrade', 'nabtrade'], ['commsec', 'CommSec'], ['fpmarkets', 'FP Markets'], ['selfwealth', 'Selfwealth'], ['stake', 'Stake'],
    ['pearler', 'Pearler'], ['cmc', 'CMC Markets'], ['ibkr', 'Interactive Brokers'], ['other', 'Another broker']]),
  GB: options([['hl', 'Hargreaves Lansdown'], ['ajbell', 'AJ Bell'], ['ii', 'interactive investor'], ['vanguard_uk', 'Vanguard UK'],
    ['trading212', 'Trading 212'], ['freetrade', 'Freetrade'], ['ibkr', 'Interactive Brokers'], ['other', 'Another broker']]),
  OTHER: options([['ibkr', 'Interactive Brokers'], ['other', 'Another broker']]),
};

const schema = z.object({
  name: z.string().trim().min(1, 'Give the account a name, like "Everyday" or "Visa".').max(120, 'Keep the name under 120 characters.'),
  type: AccountTypeSchema,
  currency: z.string().length(3, 'Choose a currency.'),
  institution: z.string().trim().max(120),
  accountNumberMasked: z.string().trim().max(40),
  // the bank code (BSB / sort code) is checked against the entity's country on submit
  bsb: z.string().trim().max(12),
  hin: z.string().trim().max(20),
  broker: z.string(),
  /** As entered: for liabilities the amount owed is positive. */
  openingBalanceCents: z.number().int().nullable(),
  openingDate: z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'Choose the date of the opening balance.'),
  interestRatePct: z.number().min(0, 'The rate can’t be negative.').max(100, 'Enter the yearly rate as a percentage, like 5.25.').nullable(),
  creditLimitCents: z.number().int().min(0, 'The limit can’t be negative.').nullable(),
  includeInNetWorth: z.boolean(),
  taxWrapper: TaxWrapperSchema,
  color: z.string().optional(),
  icon: z.string().optional(),
  notes: z.string().max(2000),
  purchasePriceCents: z.number().int().min(0, 'The price can’t be negative.').nullable(),
  purchaseDate: z.string(),
  address: z.string().trim().max(300, 'Keep it under 300 characters.'),
  propertyUse: z.enum(['owner_occupied', 'investment']),
  securedByAccountId: z.string(),
  offsetForAccountId: z.string(),
  loanIds: z.array(z.string()),
});
type Values = z.infer<typeof schema>;

/** Liabilities are stored negative (money owed) but entered as a positive amount owed. */
const shown = (type: AccountType, cents: number): number => (isLiability(type) ? -cents : cents);

const valuesFor = (account: Account | null | undefined, defaults: Partial<Account> | undefined, baseCurrency: string, loanIds: readonly string[]): Values => {
  const a = { ...defaults, ...account };
  const type = a.type ?? 'transaction';
  return {
    name: a.name ?? '', type, currency: a.currency ?? baseCurrency, institution: a.institution ?? '',
    accountNumberMasked: a.accountNumberMasked ?? '', bsb: a.bsb ?? '', hin: a.hin ?? '', broker: a.broker ?? '',
    openingBalanceCents: a.openingBalanceCents !== undefined ? shown(type, a.openingBalanceCents) : null,
    openingDate: a.openingDate ?? todayISO(),
    interestRatePct: a.interestRate != null ? Math.round(a.interestRate * 1e6) / 1e4 : null,
    creditLimitCents: a.creditLimitCents ?? null,
    includeInNetWorth: a.includeInNetWorth ?? true, taxWrapper: a.taxWrapper ?? 'none', color: a.color, icon: a.icon, notes: a.notes ?? '',
    purchasePriceCents: a.purchasePriceCents ?? null, purchaseDate: a.purchaseDate ?? '', address: a.address ?? '',
    propertyUse: a.propertyUse ?? 'owner_occupied', securedByAccountId: a.securedByAccountId ?? NONE, offsetForAccountId: a.offsetForAccountId ?? NONE,
    loanIds: [...loanIds],
  };
};

/**
 * Create or edit an account. Fields follow the account type: BSB for bank accounts, credit limit for cards,
 * interest rate for savings and debts, HIN and broker for brokerage accounts.
 */
export const AccountDialog = ({ open, onOpenChange, account, defaults, onSaved, linkLoanIds }: AccountDialogProps) => {
  const trpc = useTRPC();
  const refresh = useRefresh();
  const { entityId, entity, baseCurrency, accounts } = useLedger();
  const terms = localTerms(entity?.country);
  // offset accounts, super and HECS-HELP are Australian (API entity.features); keep the current type when editing
  const features = useFeatures();
  const typeOptions = ACCOUNT_TYPE_OPTIONS.filter((o) => features.superannuation || !AU_ONLY_TYPES.has(o.value) || o.value === account?.type);
  const editing = Boolean(account);
  const navigate = useNavigate();
  const linkedLoans = account ? accounts.filter((a) => a.securedByAccountId === account.id).map((a) => a.id) : [];
  const initial = () => valuesFor(account, defaults, baseCurrency, account ? linkedLoans : linkLoanIds ?? []);
  const form = useForm<Values>({ resolver: zodResolver(schema), defaultValues: initial() });
  const { register, control, handleSubmit, watch, reset, formState: { errors } } = form;

  useEffect(() => {
    if (open) reset(initial());
    // eslint-disable-next-line react-hooks/exhaustive-deps -- accounts.length: linked loans are known once the list loads
  }, [open, account?.id, accounts.length]);

  const setLoans = useMutation(trpc.accounts.setSecuredLoans.mutationOptions());
  const saved = async (a: Account) => {
    const loanIds = form.getValues('loanIds');
    const changed = loanIds.length !== linkedLoans.length || loanIds.some((id) => !linkedLoans.includes(id));
    if (VALUED_ASSET_TYPES.has(a.type) && changed) {
      try { await setLoans.mutateAsync({ assetId: a.id, loanIds }); } catch (err) { toast.error(`Saved ${a.name}, but the loans weren’t linked: ${errorMessage(err)}`); }
    }
    toast.success(editing ? `Saved ${a.name}` : `Added ${a.name}`);
    if (!editing && a.type === 'mortgage' && !a.securedByAccountId) promptAddProperty(a, navigate);
    void refresh();
    onSaved?.(a);
    onOpenChange(false);
  };
  const onError = (err: unknown) => toast.error(errorMessage(err));
  const create = useMutation(trpc.accounts.create.mutationOptions({ onSuccess: saved, onError }));
  const update = useMutation(trpc.accounts.update.mutationOptions({ onSuccess: saved, onError }));

  const type = watch('type');
  const liability = isLiability(type);
  const valued = VALUED_ASSET_TYPES.has(type);
  const others = accounts.filter((a) => !a.isClosed && a.id !== account?.id);
  const assetOptions = [{ value: NONE, label: 'Not secured' }, ...others.filter((a) => VALUED_ASSET_TYPES.has(a.type)).map((a) => ({ value: a.id, label: a.name }))];
  const loanChoices = others.filter((a) => SECURED_LOAN_TYPES.has(a.type));
  const offsetOptions = [{ value: NONE, label: 'None' }, ...others.filter((a) => a.type === 'mortgage' || a.type === 'loan').map((a) => ({ value: a.id, label: a.name }))];

  const submit = handleSubmit((v) => {
    if (terms.bankCode && v.bsb && !terms.bankCode.pattern.test(v.bsb)) {
      form.setError('bsb', { message: `That doesn't look like a ${terms.bankCode.label} — e.g. ${terms.bankCode.example}.` });
      return;
    }
    const body = {
      name: v.name, type: v.type, currency: v.currency,
      institution: v.institution || null, accountNumberMasked: v.accountNumberMasked || null,
      bsb: terms.bankCode && BSB_TYPES.has(v.type) && v.bsb ? v.bsb : null,
      hin: terms.brokerId && v.type === 'investment' && v.hin ? v.hin : null,
      broker: v.type === 'investment' && v.broker ? v.broker : null,
      openingBalanceCents: shown(v.type, v.openingBalanceCents ?? 0),
      openingDate: v.openingDate,
      interestRate: RATE_TYPES.has(v.type) && v.interestRatePct != null ? Number((v.interestRatePct / 100).toFixed(6)) : null,
      creditLimitCents: v.type === 'credit_card' ? v.creditLimitCents : null,
      includeInNetWorth: v.includeInNetWorth, notes: v.notes,
      taxWrapper: WRAPPER_TYPES.has(v.type) ? v.taxWrapper : 'none' as const,
      purchasePriceCents: VALUED_ASSET_TYPES.has(v.type) ? v.purchasePriceCents : null,
      purchaseDate: VALUED_ASSET_TYPES.has(v.type) && v.purchaseDate ? v.purchaseDate : null,
      address: VALUED_ASSET_TYPES.has(v.type) && v.address ? v.address : null,
      propertyUse: v.type === 'property' ? v.propertyUse : null,
      securedByAccountId: SECURED_LOAN_TYPES.has(v.type) && v.securedByAccountId !== NONE ? v.securedByAccountId : null,
      offsetForAccountId: OFFSET_SOURCE_TYPES.has(v.type) && v.offsetForAccountId !== NONE ? v.offsetForAccountId : null,
      ...(v.color ? { color: v.color } : {}), ...(v.icon ? { icon: v.icon } : {}),
    };
    if (account) update.mutate({ id: account.id, ...body });
    else if (entityId) create.mutate({ entityId, ...body, sortOrder: accounts.length });
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size="lg"
        title={editing ? `Edit ${account?.name ?? 'account'}` : 'Add an account'}
        description={editing ? undefined : 'Anything you own or owe: bank accounts, cards, loans, super, property.'}
        footer={
          <>
            <Button variant="ghost" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" form="account-form" loading={create.isPending || update.isPending || setLoans.isPending}>
              {editing ? 'Save changes' : 'Add account'}
            </Button>
          </>
        }
      >
        <form id="account-form" onSubmit={submit} noValidate className="grid gap-4 sm:grid-cols-2">
          <Field label="Name" error={errors.name?.message} className="sm:col-span-2">
            <Input {...register('name')} autoFocus placeholder="Everyday" />
          </Field>
          <Field label="Type">
            <Controller
              control={control}
              name="type"
              render={({ field }) => <Select value={field.value} onValueChange={(v) => field.onChange(v as AccountType)} options={typeOptions} />}
            />
          </Field>
          <Field label="Currency" error={errors.currency?.message}>
            <Controller
              control={control}
              name="currency"
              render={({ field }) => (
                <Select
                  value={field.value}
                  onValueChange={field.onChange}
                  options={CURRENCY_OPTIONS.some((c) => c.value === field.value) ? CURRENCY_OPTIONS : [...CURRENCY_OPTIONS, { value: field.value, label: field.value }]}
                />
              )}
            />
          </Field>
          <Field label="Institution" optional>
            <Input {...register('institution')} placeholder={entity?.country === 'GB' ? (type === 'investment' ? 'Hargreaves Lansdown' : 'Barclays') : type === 'investment' ? 'nabtrade' : 'CommBank'} />
          </Field>
          <Field label="Account number" optional hint="Last few digits are enough, e.g. ••4821.">
            <Input {...register('accountNumberMasked')} autoComplete="off" />
          </Field>
          {terms.bankCode && BSB_TYPES.has(type) ? (
            <Field label={terms.bankCode.label} optional error={errors.bsb?.message}>
              <Input {...register('bsb')} inputMode="numeric" placeholder={terms.bankCode.example} autoComplete="off" />
            </Field>
          ) : null}
          {type === 'investment' ? (
            <>
              {terms.brokerId ? (
                <Field label={terms.brokerId} optional hint="Your CHESS holder number, from a broker statement.">
                  <Input {...register('hin')} autoComplete="off" />
                </Field>
              ) : null}
              <Field label="Broker" optional>
                <Controller
                  control={control}
                  name="broker"
                  render={({ field }) => <Select value={field.value || undefined} onValueChange={field.onChange} options={BROKERS[entity?.country ?? 'AU'] ?? BROKERS.OTHER ?? []} placeholder="Choose a broker" />}
                />
              </Field>
            </>
          ) : null}
          {WRAPPER_TYPES.has(type) ? (
            <Field label="Tax wrapper" hint="Sales inside an ISA, pension, 401(k), IRA or super are tax-free: no CGT and not pooled with taxable holdings.">
              <Controller
                control={control}
                name="taxWrapper"
                render={({ field }) => <Select value={field.value} onValueChange={field.onChange} options={taxWrapperOptions(entity?.country, field.value)} />}
              />
            </Field>
          ) : null}
          <Field
            label={valued ? (editing ? 'Starting value' : 'Current value') : liability ? 'Amount owed at the start' : 'Opening balance'}
            hint={valued
              ? editing ? 'What it was worth when you started tracking it. Record a new value with Update value.' : 'What it’s worth today — an estimate is fine. Update it any time.'
              : liability ? `Enter what you owed as a positive amount; ${BRAND.name} records it as money owed.` : 'The balance on the opening date.'}
          >
            <Controller
              control={control}
              name="openingBalanceCents"
              render={({ field }) => <MoneyInput value={field.value} onChange={field.onChange} currency={watch('currency')} />}
            />
          </Field>
          <Field label={valued ? 'Value as at' : 'Opening date'} error={errors.openingDate?.message}>
            <DateInput {...register('openingDate')} />
          </Field>
          {valued ? (
            <>
              <Field label={type === 'property' ? 'Address' : 'Description'} optional error={errors.address?.message} className="sm:col-span-2">
                <Input {...register('address')} placeholder={type === 'property' ? '12 Example St, Suburb' : type === 'vehicle' ? '2021 Toyota RAV4' : 'What it is'} autoComplete="off" />
              </Field>
              <Field label="Purchase price" optional error={errors.purchasePriceCents?.message}>
                <Controller
                  control={control}
                  name="purchasePriceCents"
                  render={({ field }) => <MoneyInput value={field.value} onChange={field.onChange} allowNegative={false} currency={watch('currency')} />}
                />
              </Field>
              <Field label="Purchase date" optional>
                <DateInput {...register('purchaseDate')} />
              </Field>
              {type === 'property' ? (
                <Field label="How you use it" hint="Kept for your records and later tax hints.">
                  <Controller
                    control={control}
                    name="propertyUse"
                    render={({ field }) => (
                      <Select value={field.value} onValueChange={field.onChange} options={[{ value: 'owner_occupied', label: 'Home I live in' }, { value: 'investment', label: 'Investment property' }]} />
                    )}
                  />
                </Field>
              ) : null}
              {loanChoices.length ? (
                <fieldset className="flex flex-col gap-2 sm:col-span-2">
                  <legend className="mb-1.5 text-sm font-medium text-text">Loans on this {type === 'property' ? 'property' : 'asset'}</legend>
                  <Controller
                    control={control}
                    name="loanIds"
                    render={({ field }) => (
                      <>
                        {loanChoices.map((l) => (
                          <Checkbox
                            key={l.id}
                            label={l.name}
                            checked={field.value.includes(l.id)}
                            onCheckedChange={(c) => field.onChange(c === true ? [...field.value, l.id] : field.value.filter((x) => x !== l.id))}
                          />
                        ))}
                      </>
                    )}
                  />
                  <p className="text-xs text-text-muted">Tick every split. Offset accounts are linked from the offset account itself.</p>
                </fieldset>
              ) : null}
            </>
          ) : null}
          {SECURED_LOAN_TYPES.has(type) ? (
            <Field label="Secured against" optional hint="The property or asset this loan is for — shows your equity and LVR.">
              <Controller
                control={control}
                name="securedByAccountId"
                render={({ field }) => <Select value={field.value} onValueChange={field.onChange} options={assetOptions} />}
              />
            </Field>
          ) : null}
          {OFFSET_SOURCE_TYPES.has(type) ? (
            <Field label="Offset for" optional hint="The home loan this account offsets, e.g. CommBank Everyday Offset.">
              <Controller
                control={control}
                name="offsetForAccountId"
                render={({ field }) => <Select value={field.value} onValueChange={field.onChange} options={offsetOptions} />}
              />
            </Field>
          ) : null}
          {type === 'credit_card' ? (
            <Field label="Credit limit" optional error={errors.creditLimitCents?.message}>
              <Controller
                control={control}
                name="creditLimitCents"
                render={({ field }) => <MoneyInput value={field.value} onChange={field.onChange} allowNegative={false} currency={watch('currency')} />}
              />
            </Field>
          ) : null}
          {RATE_TYPES.has(type) ? (
            <Field label="Interest rate" optional hint="Yearly rate." error={errors.interestRatePct?.message}>
              <Controller
                control={control}
                name="interestRatePct"
                render={({ field }) => <NumberInput value={field.value} onChange={field.onChange} suffix="%" step="0.01" min={0} />}
              />
            </Field>
          ) : null}
          <div className="sm:col-span-2">
            <Controller
              control={control}
              name="includeInNetWorth"
              render={({ field }) => (
                <Switch
                  label="Include in net worth"
                  description="Turn off for accounts you want to track without counting, like a loan to a friend."
                  checked={field.value}
                  onCheckedChange={field.onChange}
                />
              )}
            />
          </div>
          <fieldset className="flex flex-col gap-1.5 sm:col-span-2">
            <legend className="mb-1.5 text-sm font-medium text-text">Colour</legend>
            <Controller control={control} name="color" render={({ field }) => <ColorPicker value={field.value} onChange={field.onChange} label="Account colour" />} />
          </fieldset>
          <fieldset className="flex flex-col gap-1.5 sm:col-span-2">
            <legend className="mb-1.5 text-sm font-medium text-text">Icon</legend>
            <Controller control={control} name="icon" render={({ field }) => <IconPicker value={field.value} onChange={field.onChange} label="Account icon" />} />
          </fieldset>
          <Field label="Notes" optional className="sm:col-span-2">
            <Textarea {...register('notes')} rows={2} />
          </Field>
        </form>
      </DialogContent>
    </Dialog>
  );
};
